import React from "react";

import { axisBottom, axisLeft } from "d3-axis";
import { select } from "d3-selection";

import { useChartContext } from "./ChartContext";
import { chartWidth, xBarHelper, yBarHelper } from "./helpers";
import styles from "./StandardBarChart.scss";

export function Axis() {
  const { svgRef, data, margin, yAxisLabel, dimensions } = useChartContext();

  React.useEffect(() => {
    const svgRefValue = svgRef.current;
    // istanbul ignore next - trivial noop and typeguard
    if (!svgRefValue) {
      return;
    }

    const svgElement = select(svgRefValue);
    const svgWidth = parseInt(svgElement.style("width"), 10);
    const svgHeight = parseInt(svgElement.style("height"), 10);

    const width = chartWidth(svgWidth, margin);
    const yAxisWidth = width - margin.left - margin.right;
    const yAxisTickText = 10;

    const xBar = xBarHelper(data, margin, width);
    const yBar = yBarHelper(data, margin, svgHeight);

    // The 'any' here is a workaround for `BaseElement` in the bellow being nullable;
    // whereas the SVG element is not nullable.

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bottomAxis: any = axisBottom(xBar)
      .tickFormat((i) => data[Number(i)].name)
      .tickSizeOuter(0);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const leftAxis: any = axisLeft(yBar).ticks(5);

    svgElement.select(".x-axis").call((g) =>
      g
        .attr("transform", `translate(0,${svgHeight - margin.bottom})`)
        .call(bottomAxis)
        .call((g) =>
          g.selectAll(".tick line").attr("class", styles["x-axis-tick-line"])
        )
        .call((g) =>
          g.selectAll(".tick text").attr("class", styles["x-axis-tick-text"])
        )
    );

    svgElement.select(".y-axis").call((g) =>
      g
        .attr("transform", `translate(${margin.left},0)`)
        .call(leftAxis)
        .call((g) => g.select(".domain").attr("class", styles["y-axis-domain"]))
        .call((g) =>
          g.selectAll(".tick text").attr("class", styles["y-axis-tick-text"])
        )
        .call((g) =>
          g
            .append("text")
            .attr("x", -margin.left)
            .attr("y", yAxisTickText)
            .attr("class", styles["y-axis-label"])
            .text(yAxisLabel)
        )
        .call((g) =>
          g
            .selectAll(".tick line")
            .attr("x2", yAxisWidth)
            .attr("class", styles["y-axis-level-marker"])
        )
    );
  }, [dimensions]);

  return (
    <>
      <g className="x-axis" data-testid="chart-x-axis" />
      <g className="y-axis" data-testid="chart-y-axis" />
    </>
  );
}
